import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { WatchSelectors } from "../../../state";
import {
  Typography,
  Box,
  Button,
  Chip,
  Tooltip,
  DialogContent,
  Dialog,
  CardContent,
  Card,
} from "@material-ui/core";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { green, red, blue, purple, brown, grey } from "@material-ui/core/colors";
import { format } from "date-fns";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

const statusColor = [
  { status_id: 1, status: "Active", color: green[600] },
  { status_id: 3, status: "Backordered", color: purple[500] },
  { status_id: 5, status: "Coming Soon", color: brown[500] },
  { status_id: 0, status: "Disabled", color: red[700] },
  { status_id: 2, status: "Discontinued", color: blue[500] },
  { status_id: 4, status: "Hidden", color: grey[500] },
];

export const WatchHeader = (props) => {
  const [openStats, setOpenStats] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const [copied, setCopied] = useState(false);
  const sentinelRef = React.useRef(null);
  const { values } = useFormikContext();
  const { brand: brand_list, series: series_list } = useSelector(WatchSelectors.watches);

  const brand = brand_list?.find((x) => x.brand_id === values.brand_id)?.brand || "";
  const series = series_list?.find((x) => x.series_id === values.series_id)?.series || "";
  const dynamicTitle = [brand, series, values.model_no, values.model_no_1]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  if (!props.isEdit) return null;

  const activeStatusId = values.status_id ?? props.statusID ?? props.data.status_id;
  const currentStatus = statusColor.find((x) => x.status_id === activeStatusId);

  return (
    <>
      <div ref={sentinelRef} style={{ height: 0 }} />
      <Box
        id="watch-sticky-header"
        mb={1.5}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          ...(isStuck && {
            backgroundColor: "#fff",
            paddingTop: 10,
            paddingBottom: 6,
            paddingLeft: 16,
            paddingRight: 16,
            boxShadow: "0 4px 6px -2px rgba(0,0,0,0.15)",
          }),
        }}
      >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {/* Left: title + date */}
        <Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="h6" style={{ fontWeight: 600, lineHeight: 1.2, fontSize: 19 }}>
              {dynamicTitle || props.data.default_title}
            </Typography>
            <Tooltip title={copied ? "Copied!" : "Copy ID"} placement="top">
              <Chip
                label={`ID: ${props.watchId}`}
                size="small"
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(String(props.watchId));
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                style={{ fontSize: 11, height: 20, cursor: "pointer" }}
              />
            </Tooltip>
          </Box>
          {props.data.act_date_added && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 2, fontSize: 12 }}>
              Added {format(new Date(props.data.act_date_added), "MMM d, yyyy")}
              {props.data.user
                ? ` · ${props.data.user.first_name} ${props.data.user.last_name}`
                : ""}
            </Typography>
          )}
        </Box>

        {/* Right: actions top, status bottom */}
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Box display="flex" alignItems="center">
            <Button
              size="small"
              color="primary"
              endIcon={<EqualizerIcon style={{ fontSize: 12 }} />}
              onClick={() => setOpenStats(true)}
              style={{ fontSize: 12, padding: "2px 6px" }}
            >
              Statistics
            </Button>
            <span style={{ width: 1, alignSelf: "stretch", backgroundColor: "rgba(0,0,0,0.12)", margin: "4px 2px" }} />
            <Button
              size="small"
              color="primary"
              endIcon={<OpenInNewIcon style={{ fontSize: 12 }} />}
              onClick={() => window.open(`/history/${props.watchId}`, "_blank")}
              style={{ fontSize: 12, padding: "2px 6px" }}
            >
              History
            </Button>
            <span style={{ width: 1, alignSelf: "stretch", backgroundColor: "rgba(0,0,0,0.12)", margin: "4px 2px" }} />
            <Button
              size="small"
              color="primary"
              endIcon={<OpenInNewIcon style={{ fontSize: 12 }} />}
              onClick={() => {
                const siteUrl = process.env.REACT_APP_COMPANY_SITE_URL || 'https://prestigetime7.bitbean.dev';
                // Use the SERVER-side (persisted) stub, not the Formik in-memory
                // one. WatchModel.tsx's auto-regen useEffect rewrites
                // `values.seo_stub` on load when the current model_no_1/2 differ
                // from what the stub was baked with — that regenerated value
                // hasn't been saved yet, so it 404s on the storefront until the
                // user clicks Update. `data.seo_stub` reflects what the router
                // table actually holds → guaranteed live URL.
                const liveStub = props.data.seo_stub || values.seo_stub;
                window.open(
                  `${siteUrl}/${liveStub}.html`,
                  "_blank",
                );
              }}
              style={{ fontSize: 12, padding: "2px 6px" }}
            >
              Website
            </Button>
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 6, marginTop: 2, alignSelf: "stretch", justifyContent: "flex-end" }}>
            {/* Only surface the retirement badge for ACTIVE watches — a disabled/
                hidden/etc. watch being retired isn't noteworthy. */}
            {props.retired && Number(activeStatusId) === 1 && (
              <Chip
                label="Marked For Retirement"
                size="small"
                style={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 400,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                  backgroundColor: "#fad0de",
                  color: "#880e4f",
                  marginRight: "auto",
                  marginLeft: 5,
                }}
              />
            )}
            <FiberManualRecordIcon style={{ color: currentStatus?.color, fontSize: 12 }} />
            <Typography variant="body2" style={{ color: currentStatus?.color, fontWeight: 500, fontSize: 12 }}>
              {currentStatus?.status}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={openStats}
        onClose={() => setOpenStats(false)}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Statistics
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Box m={1}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Page Views
                  </Typography>
                  <Typography variant="h5" component="h2">
                    {props.data.item_stat?.viewed || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box m={1}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Added to Cart
                  </Typography>
                  <Typography variant="h5" component="h2">
                    {props.data.item_stat?.added_to_cart || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box m={1}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Conversion
                  </Typography>
                  <Typography variant="h5" component="h2">
                    {props.data.item_stat?.viewed
                      ? (
                          (100 * props.data.item_stat.added_to_cart) /
                          props.data.item_stat.viewed
                        ).toFixed(2)
                      : 0}
                    %
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
    </>
  );
};
